// ---------------------------------------------------------------------------
// PWA Caisse Tablette — Tickets Screen (transaction history)
// Time filters + ticket list + detail modal + receipt modal
// ---------------------------------------------------------------------------

import { useState, useEffect, useCallback, useMemo } from 'react';
import TicketList from '@/components/TicketList';
import ReceiptModal from '@/components/ReceiptModal';
import { useAppStore } from '@/store/app-store';
import { apiClient } from '@/services/api-client';
import { formatPrice } from '@/lib/price-formatter';
import type { TicketData } from '@/components/TicketCard';

type TimeRange = 'today' | 'yesterday' | 'week' | 'month';

const TIME_FILTERS: { key: TimeRange; label: string }[] = [
  { key: 'today', label: "Aujourd'hui" },
  { key: 'yesterday', label: 'Hier' },
  { key: 'week', label: 'Cette semaine' },
  { key: 'month', label: 'Ce mois' },
];

const CANCEL_REASONS = [
  'Erreur de saisie',
  'Client change d\'avis',
  'Probleme paiement',
];

export default function TicketsScreen() {
  const showToast = useAppStore((s) => s.showToast);

  const [timeRange, setTimeRange] = useState<TimeRange>('today');
  const [tickets, setTickets] = useState<TicketData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState<TicketData | null>(null);
  const [receiptTransactionId, setReceiptTransactionId] = useState<number | string | null>(null);
  const [isDuplicate, setIsDuplicate] = useState(false);

  // Cancel modal state
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [isCancelling, setIsCancelling] = useState(false);

  // Load tickets
  const loadTickets = useCallback(async () => {
    setIsLoading(true);
    try {
      // For now the API returns today's tickets; we filter client-side for other ranges
      const response = await apiClient.getTodayTickets();
      const data = (response.transactions ?? []) as TicketData[];

      // Sort descending by date
      data.sort((a, b) => {
        const da = new Date(a.timestamp ?? a.client_created_at ?? 0).getTime();
        const db = new Date(b.timestamp ?? b.client_created_at ?? 0).getTime();
        return db - da;
      });

      setTickets(data);
    } catch {
      showToast('Erreur chargement tickets', 'error');
      setTickets([]);
    } finally {
      setIsLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets, timeRange]);

  // Stats
  const stats = useMemo(() => {
    const count = tickets.length;
    const total = tickets.reduce((sum, t) => sum + (t.total ?? t.total_amount ?? 0), 0);
    const avg = count > 0 ? total / count : 0;
    const items = tickets.reduce(
      (sum, t) => sum + (t.items_count ?? t.items?.length ?? 0),
      0,
    );
    return { count, total, avg, items };
  }, [tickets]);

  // ---- Detail actions ----

  const handleViewReceipt = () => {
    if (!selectedTicket) return;
    const txId = selectedTicket.id ?? selectedTicket.transaction_id;
    if (!txId) {
      showToast('ID transaction manquant', 'error');
      return;
    }
    setIsDuplicate(false);
    setReceiptTransactionId(txId);
    setSelectedTicket(null);
  };

  const handleDuplicate = () => {
    if (!selectedTicket) return;
    const txId = selectedTicket.id ?? selectedTicket.transaction_id;
    if (!txId) {
      showToast('ID transaction manquant', 'error');
      return;
    }
    setIsDuplicate(true);
    setReceiptTransactionId(txId);
    setSelectedTicket(null);
  };

  const handleOpenCancel = () => {
    setCancelReason('');
    setCustomReason('');
    setShowCancelModal(true);
  };

  const handleConfirmCancel = async () => {
    if (!selectedTicket) return;
    const txId = selectedTicket.id ?? selectedTicket.transaction_id;
    if (!txId) return;

    const reason = cancelReason === 'other' ? customReason.trim() : cancelReason;
    if (!reason) {
      showToast('Selectionnez une raison', 'warning');
      return;
    }

    setIsCancelling(true);
    try {
      await apiClient.refundTransaction(String(txId), reason);
      showToast('Vente annulee -- ecriture negative creee', 'success');
      setShowCancelModal(false);
      setSelectedTicket(null);
      loadTickets();
    } catch (err) {
      showToast(`Erreur annulation: ${(err as Error).message}`, 'error');
    } finally {
      setIsCancelling(false);
    }
  };

  const canCancel =
    selectedTicket?.status === 'completed' && !selectedTicket?.is_cancellation;

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 shrink-0">
        <h2 className="text-base font-semibold text-white">Historique Tickets</h2>
        <button
          type="button"
          onClick={loadTickets}
          className="text-gray-400 hover:text-white p-1"
          aria-label="Rafraichir"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="23 4 23 10 17 10" />
            <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
          </svg>
        </button>
      </div>

      {/* Time filters */}
      <div className="flex gap-2 px-4 py-2 overflow-x-auto shrink-0">
        {TIME_FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setTimeRange(f.key)}
            className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              timeRange === f.key
                ? 'bg-accent text-white'
                : 'bg-surface-card text-gray-300 hover:bg-surface-hover'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-2 px-4 py-2 shrink-0">
        {[
          { label: 'Tickets', value: String(stats.count) },
          { label: 'Total', value: formatPrice(stats.total) },
          { label: 'Moyenne', value: formatPrice(stats.avg) },
          { label: 'Articles', value: String(stats.items) },
        ].map((s) => (
          <div key={s.label} className="bg-surface-card rounded-xl p-2.5 text-center">
            <p className="text-sm font-bold text-accent">{isLoading ? '...' : s.value}</p>
            <p className="text-[10px] text-gray-400">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Ticket list */}
      <TicketList
        tickets={tickets}
        isLoading={isLoading}
        onSelect={setSelectedTicket}
      />

      {/* Ticket detail modal */}
      {selectedTicket && !showCancelModal && (
        <div className="fixed inset-0 z-[200] flex items-end md:items-center justify-center bg-black/60 animate-fade-in">
          <div className="bg-surface rounded-t-2xl md:rounded-xl w-full max-w-md max-h-[85vh] overflow-y-auto animate-slide-up">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <h3 className="text-sm font-semibold text-white">
                Ticket #{String(selectedTicket.id ?? selectedTicket.transaction_id ?? 'N/A').slice(-6)}
              </h3>
              <button
                type="button"
                onClick={() => setSelectedTicket(null)}
                className="text-gray-400 hover:text-white"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            {/* Items */}
            <div className="p-4">
              <h4 className="text-xs text-gray-400 mb-2 font-semibold">Articles</h4>
              {(selectedTicket.items ?? []).length > 0 ? (
                <div className="space-y-1.5 mb-4">
                  {(selectedTicket.items ?? []).map((item, idx) => (
                    <div key={idx} className="flex justify-between text-sm border-b border-white/5 pb-1.5">
                      <span className="text-white flex-1">{item.name}</span>
                      <span className="text-gray-400 mx-3">x{item.quantity}</span>
                      <span className="text-white font-medium">
                        {formatPrice(item.price * item.quantity)}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm mb-4">Aucun article</p>
              )}

              {/* Payments */}
              {(selectedTicket.payments ?? []).length > 0 && (
                <>
                  <h4 className="text-xs text-gray-400 mb-2 font-semibold">Paiements</h4>
                  <div className="space-y-1.5 mb-4">
                    {(selectedTicket.payments ?? []).map((p, idx) => (
                      <div key={idx} className="flex justify-between text-sm">
                        <span className="text-gray-300">{p.type}</span>
                        <span className="text-white">{formatPrice(p.amount)}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Total */}
              <div className="flex justify-between text-base font-bold border-t-2 border-white/10 pt-2">
                <span className="text-white">TOTAL</span>
                <span className="text-success">
                  {formatPrice(selectedTicket.total ?? selectedTicket.total_amount ?? 0)}
                </span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-2 px-4 py-3 border-t border-white/10">
              <button
                type="button"
                onClick={() => setSelectedTicket(null)}
                className="btn-secondary text-xs py-2 px-3"
              >
                Fermer
              </button>
              <button
                type="button"
                onClick={handleViewReceipt}
                className="btn-primary text-xs py-2 px-3"
              >
                Ticket fiscal
              </button>
              <button
                type="button"
                onClick={handleDuplicate}
                className="btn-secondary text-xs py-2 px-3"
              >
                Duplicata
              </button>
              {canCancel && (
                <button
                  type="button"
                  onClick={handleOpenCancel}
                  className="btn-danger text-xs py-2 px-3"
                >
                  Annuler
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Cancel modal (NF525) */}
      {showCancelModal && selectedTicket && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/60 animate-fade-in">
          <div className="bg-surface rounded-xl w-full max-w-sm mx-4 animate-slide-up">
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <h3 className="text-sm font-semibold text-white">Annuler cette vente</h3>
              <button
                type="button"
                onClick={() => setShowCancelModal(false)}
                className="text-gray-400 hover:text-white"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="p-4">
              {/* Warning */}
              <div className="bg-warning/10 border border-warning/30 rounded-xl p-3 mb-4 text-xs text-warning">
                <strong>Action irreversible</strong> -- une ecriture negative sera creee dans le journal fiscal.
              </div>

              {/* Reason selection */}
              <p className="text-sm text-gray-300 mb-2 font-medium">
                Raison de l'annulation <span className="text-danger">*</span>
              </p>
              <div className="space-y-2 mb-3">
                {CANCEL_REASONS.map((reason) => (
                  <label
                    key={reason}
                    className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                      cancelReason === reason
                        ? 'border-accent bg-accent/10'
                        : 'border-white/10 bg-surface-card hover:bg-surface-hover'
                    }`}
                  >
                    <input
                      type="radio"
                      name="cancel-reason"
                      value={reason}
                      checked={cancelReason === reason}
                      onChange={() => setCancelReason(reason)}
                      className="accent-accent"
                    />
                    <span className="text-sm text-white">{reason}</span>
                  </label>
                ))}
                <label
                  className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                    cancelReason === 'other'
                      ? 'border-accent bg-accent/10'
                      : 'border-white/10 bg-surface-card hover:bg-surface-hover'
                  }`}
                >
                  <input
                    type="radio"
                    name="cancel-reason"
                    value="other"
                    checked={cancelReason === 'other'}
                    onChange={() => setCancelReason('other')}
                    className="accent-accent"
                  />
                  <span className="text-sm text-white">Autre</span>
                </label>
              </div>

              {cancelReason === 'other' && (
                <textarea
                  value={customReason}
                  onChange={(e) => setCustomReason(e.target.value)}
                  placeholder="Preciser la raison..."
                  rows={2}
                  className="w-full bg-surface-card text-white rounded-xl px-3 py-2 text-sm outline-none border border-white/10 focus:border-accent resize-none mb-3"
                />
              )}
            </div>

            <div className="flex gap-2 px-4 py-3 border-t border-white/10">
              <button
                type="button"
                onClick={() => setShowCancelModal(false)}
                className="btn-secondary flex-1 text-xs py-2"
              >
                Retour
              </button>
              <button
                type="button"
                onClick={handleConfirmCancel}
                disabled={isCancelling || (!cancelReason || (cancelReason === 'other' && !customReason.trim()))}
                className="btn-danger flex-1 text-xs py-2 disabled:opacity-50"
              >
                {isCancelling ? 'Annulation...' : 'Confirmer l\'annulation'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Receipt modal */}
      {receiptTransactionId && (
        <ReceiptModal
          transactionId={receiptTransactionId}
          isDuplicate={isDuplicate}
          onClose={() => setReceiptTransactionId(null)}
        />
      )}
    </div>
  );
}
